/**
 * Editor.js inline tool: link a selected chunk of narrative text to a
 * "finding". POC for the narrative editor — see
 * _planning/narrative_editor_poc.md.
 *
 * Flow:
 *  1. User selects text in the editor → the inline toolbar appears with a
 *     "Link to finding" button (this tool's render()).
 *  2. Clicking it calls surround(range). We clone+stash the Range, then ask
 *     the host (Vue page) to open a finding-picker dialog via the injected
 *     `requestFinding` callback. Opening the dialog blows away the live DOM
 *     selection, which is exactly why we hold our own cloned Range.
 *  3. When the dialog resolves with a finding, we wrap the stashed range in a
 *     special <a> whose href/attributes encode the finding.
 *
 * The href format is intentionally a single TBD helper (buildFindingHref) so
 * the real scheme can be swapped in one place once it's decided.
 */
import type { API, InlineTool } from '@editorjs/editorjs';
// Use the same icon set Editor.js ships, so the toolbar button matches the
// built-in tools' glyph size and weight exactly.
import { IconStar } from '@codexteam/icons';

export interface Finding {
    id: string;
    /** Short human code shown to the user, e.g. "F-014". */
    code: string;
    label: string;
}

export interface FindingLinkConfig {
    /**
     * Open the finding picker and resolve with the chosen finding, or null if
     * the user cancels. Provided by the host component.
     */
    requestFinding: () => Promise<Finding | null>;
}

/**
 * Custom `pepper-finding:<id>` URI scheme for a finding link. Centralized here
 * so the scheme lives in exactly one place.
 *
 * It is deliberately NOT a navigable http(s) URL: a finding has no standalone
 * page, and the durable key is the finding id (mirrored onto `data-finding-id`,
 * which is what the backend scans to derive a page's link set). The custom
 * scheme keeps the anchor inert on plain click while remaining an unambiguous,
 * parseable marker — the host intercepts clicks on `.finding-link` to reveal
 * the finding in the side panel rather than letting the browser navigate.
 */
export const FINDING_HREF_SCHEME = 'pepper-finding';

export function buildFindingHref(finding: Finding): string {
    return `${FINDING_HREF_SCHEME}:${finding.id}`;
}

/**
 * Inverse of {@link buildFindingHref}: pull the finding id out of an anchor's
 * href, or null if it isn't a finding link. Prefer the `data-finding-id`
 * attribute when reading from a live element; this is for href-only contexts.
 */
export function parseFindingHref(href: string | null | undefined): string | null {
    if (!href) return null;
    const prefix = `${FINDING_HREF_SCHEME}:`;
    return href.startsWith(prefix) ? href.slice(prefix.length) : null;
}

export class FindingLinkTool implements InlineTool {
    static get isInline(): boolean {
        return true;
    }

    static get title(): string {
        return 'Link to finding';
    }

    /** Allow our anchors (and their data-* attrs) to survive save sanitization. */
    static get sanitize() {
        return {
            a: {
                class: true,
                href: true,
                title: true,
                'data-finding-id': true,
                'data-finding-code': true,
                'data-finding-label': true,
            },
        };
    }

    private api: API;
    private config: FindingLinkConfig;
    private savedRange: Range | null = null;

    constructor({ api, config }: { api: API; config: FindingLinkConfig }) {
        this.api = api;
        this.config = config;
    }

    render(): HTMLElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add('ce-inline-tool');
        button.innerHTML = IconStar;
        return button;
    }

    /**
     * Called by Editor.js when the toolbar button is clicked with a live
     * selection. We don't act synchronously — we stash the range and defer to
     * the async dialog.
     */
    surround(range: Range): void {
        if (!range) return;
        this.savedRange = range.cloneRange();
        void this.linkToFinding();
    }

    private async linkToFinding(): Promise<void> {
        const range = this.savedRange;
        this.savedRange = null;
        if (!range) return;

        const finding = await this.config.requestFinding();
        if (!finding) return;

        this.wrapRange(range, finding);
    }

    private wrapRange(range: Range, finding: Finding): void {
        const anchor = document.createElement('a');
        anchor.href = buildFindingHref(finding);
        anchor.classList.add('finding-link');
        anchor.title = `Finding ${finding.code}: ${finding.label}`;
        anchor.dataset.findingId = finding.id;
        anchor.dataset.findingCode = finding.code;
        anchor.dataset.findingLabel = finding.label;

        // extractContents + insertNode (rather than surroundContents) so a
        // selection that crosses inline boundaries still links cleanly.
        const contents = range.extractContents();
        anchor.appendChild(contents);
        range.insertNode(anchor);

        // Drop the live selection so the freshly inserted link isn't left
        // highlighted.
        const selection = window.getSelection();
        selection?.removeAllRanges();
    }

    /** Light up the button when the caret sits inside an existing finding link. */
    checkState(): boolean {
        const anchor = this.api.selection.findParentTag('A', 'finding-link');
        return anchor !== null;
    }
}
